import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type ITabItem } from 'shared/ui'
import { CampaignApi, type IResponseFullCampaign } from 'entities/Campaign'
import { getSendFromInfo } from 'entities/SendFrom'
import { ContactFiltersApi } from 'entities/Contacts/api/contactsFilters'
import { TagsApi } from 'entities/Tags'
import { Tag } from 'entities/Tags/model/Tag'
import { Segment } from 'entities/Segment/model/Segment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { CustomFieldsStore } from 'entities/CustomField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { EnrollmentFilters, EnrollmentStore } from 'widgets/ContactEnrollment'
import { EnumEnrollmentMode } from 'widgets/ContactEnrollment/store/types'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { type ITabKey } from './types'
import { CampaignScheduleStore } from './campaignScheduleStore'

export class CampaignDetailsModalStore {
  private _enrollmentFilters = EnrollmentFilters.Empty()
  private _enrollmentStore: EnrollmentStore
  private _customFieldsStore = new CustomFieldsStore()
  private _id: null | number | string = null
  private _loading = true
  private _data: IResponseFullCampaign | null = null
  private _activeTabKey: ITabKey = 'enrollContacts'

  private _disposeLoadCampaign: IReactionDisposer | null = null
  private _disposeEnrollmentFilters: IReactionDisposer | null = null

  constructor(private _entities: IEntitiesStore) {
    this._enrollmentStore = new EnrollmentStore(
      {
        filterConfig: {
          makeRequest: () => {
            return ContactFiltersApi.getFilters({
              workflowSource: 'campaign',
              skipValues: ['tag'],
            }).then(({ data }) => data)
          },
        },
        searchDropdownConfig: {
          includeItemsType: [SearchTypesEnum.tags, SearchTypesEnum.contacts],
          placeholder: 'Search tag or contact',
        },
      },
      this._entities
    )
    this._loadCampaignReaction()
    this._enrollmentFiltersReaction()
    this._initFilters()

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get data() {
    return this._data
  }

  get activeTabKey() {
    return this._activeTabKey
  }

  get enrolledContactsCount() {
    return this._data?.analytics?.enrolled_contacts_count || 0
  }

  get failedCount() {
    return this._data?.analytics?.failed_count || 0
  }

  get stoppedByUserCount() {
    return this._data?.analytics?.stopped_by_user_count || 0
  }

  get skippedCount() {
    return this.failedCount + this.stoppedByUserCount
  }

  get steps() {
    return this.getSteps()
  }

  getSteps = () => {
    const steps = this._data?.steps.map((step) => {
      const attachments: Attachment[] = []

      if (Array.isArray(step.payload.media_url)) {
        step.payload.media_url.map((media) => {
          const attachment = new Attachment({ responseMediaShort: media })
          attachments.push(attachment)
        })
      }

      const number = this._entities.numbersStore.getItem(step.send_from?.options.number_id)
      let team = this._entities.inboxesStore.getItem(step.send_from?.options.team_id)

      if (!team && number) {
        team = this._entities.inboxesStore.inboxesList.find(
          (inbox) => inbox.type === 'inbox' && inbox.numberIds.includes(number.id)
        )
      }

      const sendFromInfo = getSendFromInfo(step.send_from, {
        number,
        team: team?.type === 'inbox' ? team : undefined,
      })

      const scheduleStore = new CampaignScheduleStore()

      const dateFiled =
        this._customFieldsStore.fields.find((field) =>
          step.schedule?.type === 'date_field'
            ? field.key === step.schedule?.options?.date_field
              ? field
              : null
            : null
        ) || null

      scheduleStore.init(step.schedule, dateFiled, this._data?.default_timezone)

      return { ...step, attachments, sendFromInfo, scheduleStore }
    })

    return steps || []
  }

  get tabs(): ITabItem<ITabKey>[] {
    return [
      {
        key: 'enrollContacts',
        name: 'Enroll your contacts',
      },
      {
        key: 'steps',
        name: 'Steps',
      },
    ]
  }

  setActiveTab = (value: ITabItem<ITabKey>) => {
    this._activeTabKey = value.key
  }

  setId = (id: number | string) => {
    this._id = id
  }

  viewEnrollment = async () => {
    const filters = this._enrollmentFilters.clone()

    const cancelFilters = () => Promise.resolve(true)

    const confirmFilters = async (newFilters: EnrollmentFilters) => {
      runInAction(() => {
        this._enrollmentFilters = newFilters
      })
      return true
    }

    this._enrollmentStore.openModal({
      filters: filters,
      action: {
        cancel: cancelFilters,
        confirm: confirmFilters,
      },
      allowEmptyCount: true,
      title: 'Contacts',
    })
  }

  private _init = async () => {
    this._enrollmentStore.setMode(EnumEnrollmentMode.ViewOnly)
    await Promise.all([this._initCampaign(), this._initFilters(), this._initCustomFields()])
  }

  private _initCustomFields = async () => {
    await this._customFieldsStore.loadData({ types: ['date', 'datetime'], all: true })
  }

  private _initFilters = async () => {
    this._enrollmentStore.filterEditorStore.initFilters()
  }

  private _initCampaign = async () => {
    if (!this._id) return

    try {
      const {
        data: { data },
      } = await CampaignApi.getById(this._id)
      this._data = data
    } catch (e) {
      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  private _initEnrollment = async (filtersListGroups: IResponseFullCampaign['filters']) => {
    if (!filtersListGroups) return
    try {
      this._loading = true
      const tagsIds = filtersListGroups.tags || []
      const contactsIds = filtersListGroups.contacts || []
      const segmentsIds = filtersListGroups.segments || []

      const [tags, contacts] = await Promise.all([
        this.fetchTags(tagsIds),
        this._entities.contactsStore.getByIds(contactsIds),
      ])
      const segments = segmentsIds.reduce<Segment[]>((state, id) => {
        const item = this._entities.segmentsStore.getItem(id)

        if (item) {
          state.push(item)
        }

        return state
      }, [])
      const advanced = filtersListGroups.advanced?.map(
        (item) =>
          new Segment({
            id: -1,
            organization_id: 0,
            name: '',
            filters: item,
            is_favorite: false,
          })
      )

      this._enrollmentFilters.sync({
        tags: tags,
        segments: segments,
        contacts: contacts,
        filters: advanced,
      })
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  fetchTags = async (ids: number[]) => {
    const tags = await TagsApi.getTagsById(ids)

    return tags.map((tag) => new Tag(tag))
  }

  private _loadCampaignReaction = () => {
    this._disposeLoadCampaign?.()

    this._disposeLoadCampaign = reaction(
      () => this._id,
      () => this._init()
    )
  }

  private _enrollmentFiltersReaction = () => {
    this._disposeEnrollmentFilters?.()

    this._disposeEnrollmentFilters = reaction(
      () => this._data?.filters,
      (value) => {
        if (value) {
          this._initEnrollment(value)
        }
      }
    )
  }
}
